import {
  AboutResponseSchema,
  CloudKeysResponseSchema,
  IdleStopResponseSchema,
  DeleteFilesResponseSchema,
  DeleteModelResponseSchema,
  MachineFilesSchema,
  InstalledModelsSchema,
  type SetModelFamilyRequest,
  type DeleteFilesRequest,
  type DeleteModelRequest,
  type MachineFileKind,
  AdminUserResponseSchema,
  AdminUsersResponseSchema,
  CreateInviteResponseSchema,
  ErrorBodySchema,
  InviteLookupResponseSchema,
  InvitesResponseSchema,
  MachineResponseSchema,
  KnowledgeListResponseSchema,
  KnowledgeTopicResponseSchema,
  MachinesResponseSchema,
  OpenMachineResponseSchema,
  DeviceSessionsResponseSchema,
  PushKeyResponseSchema,
  StartersResponseSchema,
  WakeResponseSchema,
  BatchResponseSchema,
  type StartBatchRequest,
  ChatsResponseSchema,
  ChatResponseSchema,
  ChatDetailResponseSchema,
  type CreateChatRequest,
  type SendChatMessageRequest,
  AuthorizeInfoResponseSchema,
  AuthorizeDecisionResponseSchema,
  ConnectedAppsResponseSchema,
  WorkflowTemplatesResponseSchema,
  ImportWorkflowResponseSchema,
  type ImportWorkflowRequest,
  InputImageResponseSchema,
  NodePacksResponseSchema,
  NodePackSearchResponseSchema,
  NodeInstallResponseSchema,
  NodeInstallsResponseSchema,
  AccountSettingsResponseSchema,
  type AccountSettings,
  InputImagesResponseSchema,
  RecipeSharesResponseSchema,
  RecipeShareResponseSchema,
  SharedRecipesResponseSchema,
  SharedRecipeDetailSchema,
  type CopySharedRequest,
  type FromStarterRequest,
  GalleryImageResponseSchema,
  GalleryResponseSchema,
  RecipeDetailResponseSchema,
  RecipeResponseSchema,
  RecipesResponseSchema,
  RunResponseSchema,
  RunsResponseSchema,
  ValidationResultSchema,
  type OpenMachineRequest,
  type StartRunRequest,
  type ValidateRecipeRequest,
  PairingResponseSchema,
  RestartMachineResponseSchema,
  CreatePairingResponseSchema,
  ProviderTokensResponseSchema,
  ApiTokensResponseSchema,
  CreateApiTokenResponseSchema,
  CatalogResponseSchema,
  DownloadResponseSchema,
  DownloadsResponseSchema,
  LogsResponseSchema,
  MachineStatusResponseSchema,
  UpdateAgentResponseSchema,
  UpdateComfyResponseSchema,
  SetupStatusSchema,
  UserResponseSchema,
  type ChangePasswordRequest,
  type CreateInviteRequest,
  type ErrorDetail,
  type LoginRequest,
  type CreatePairingRequest,
  type Provider,
  type StartDownloadRequest,
  type RedeemInviteRequest,
  type SetupRequest,
} from '@couch/shared';
import type { z } from 'zod';

/** Every failed API call becomes one of these, carrying the server's code and field details. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: readonly ErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** First message per field, for showing next to inputs. */
  fieldErrors(): Record<string, string> {
    const fields: Record<string, string> = {};
    for (const d of this.details) fields[d.path] ??= d.message;
    return fields;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function send(method: Method, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, init);
  } catch {
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.');
  }
  if (!res.ok) throw await toApiError(res);
  return res;
}

async function toApiError(res: Response): Promise<ApiError> {
  const data: unknown = await res.json().catch(() => null);
  const envelope = ErrorBodySchema.safeParse(data);
  if (envelope.success) {
    const { code, message, details } = envelope.data.error;
    return new ApiError(res.status, code, message, details ?? []);
  }
  return new ApiError(res.status, 'server_error', `The server answered ${res.status}. Try again in a moment.`);
}

export async function json<S extends z.ZodType>(schema: S, method: Method, path: string, body?: unknown): Promise<z.infer<S>> {
  const res = await send(method, path, body);
  const parsed = schema.safeParse(await res.json().catch(() => null));
  if (!parsed.success) throw new ApiError(res.status, 'bad_response', 'Unexpected response from the server.');
  return parsed.data;
}

/** A file as the raw request body (doc 33); the server checks what it really is. */
async function upload<S extends z.ZodType>(schema: S, path: string, file: Blob): Promise<z.infer<S>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': file.type || 'application/octet-stream' }, body: file });
  } catch {
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.');
  }
  if (!res.ok) throw await toApiError(res);
  const parsed = schema.safeParse(await res.json().catch(() => null));
  if (!parsed.success) throw new ApiError(res.status, 'bad_response', 'Unexpected response from the server.');
  return parsed.data;
}

export async function empty(method: Method, path: string, body?: unknown): Promise<void> {
  await send(method, path, body);
}

const seg = encodeURIComponent;

/** The typed contract with the server. No fetch() anywhere else in the web app. */
export const api = {
  setupStatus: () => json(SetupStatusSchema, 'GET', '/auth/setup'),
  setup: (body: SetupRequest) => json(UserResponseSchema, 'POST', '/auth/setup', body),
  login: (body: LoginRequest) => json(UserResponseSchema, 'POST', '/auth/login', body),
  logout: () => empty('POST', '/auth/logout'),
  me: () => json(UserResponseSchema, 'GET', '/auth/me'),
  changePassword: (body: ChangePasswordRequest) => empty('POST', '/auth/password', body),
  lookupInvite: (token: string) => json(InviteLookupResponseSchema, 'GET', `/invites/${seg(token)}`),
  redeemInvite: (token: string, body: RedeemInviteRequest) => json(UserResponseSchema, 'POST', `/invites/${seg(token)}/redeem`, body),
  createInvite: (body: CreateInviteRequest) => json(CreateInviteResponseSchema, 'POST', '/admin/invites', body),
  listInvites: () => json(InvitesResponseSchema, 'GET', '/admin/invites'),
  revokeInvite: (id: string) => empty('DELETE', `/admin/invites/${seg(id)}`),
  listUsers: () => json(AdminUsersResponseSchema, 'GET', '/admin/users'),
  setPreviewSpace: (id: string, limited: boolean) => json(AdminUserResponseSchema, 'PUT', `/admin/users/${seg(id)}/preview-space`, { limited }),
  setUserDisabled: (id: string, disabled: boolean) =>
    json(AdminUserResponseSchema, 'POST', `/admin/users/${seg(id)}/${disabled ? 'disable' : 'enable'}`),
  listMachines: () => json(MachinesResponseSchema, 'GET', '/machines'),
  knowledge: () => json(KnowledgeListResponseSchema, 'GET', '/knowledge'),
  about: () => json(AboutResponseSchema, 'GET', '/about'),
  cloudKeys: () => json(CloudKeysResponseSchema, 'GET', '/cloud/keys'),
  setRunPodKey: (key: string) => empty('PUT', '/cloud/keys/runpod', { key, acknowledged: true }),
  removeRunPodKey: () => empty('DELETE', '/cloud/keys/runpod'),
  knowledgeTopic: (id: string) => json(KnowledgeTopicResponseSchema, 'GET', `/knowledge/${seg(id)}`),
  createPairing: (body: CreatePairingRequest) => json(CreatePairingResponseSchema, 'POST', '/machines/pairings', body),
  getPairing: (id: string) => json(PairingResponseSchema, 'GET', `/machines/pairings/${seg(id)}`),
  renameMachine: (id: string, name: string) => json(MachineResponseSchema, 'PATCH', `/machines/${seg(id)}`, { name }),
  removeMachine: (id: string) => empty('DELETE', `/machines/${seg(id)}`),
  openMachine: (id: string, body: OpenMachineRequest = {}) => json(OpenMachineResponseSchema, 'POST', `/machines/${seg(id)}/open`, body),
  wakeMachine: (id: string) => json(WakeResponseSchema, 'POST', `/machines/${seg(id)}/wake`, {}),
  restartMachine: (id: string) => json(RestartMachineResponseSchema, 'POST', `/machines/${seg(id)}/restart`),
  providerTokens: () => json(ProviderTokensResponseSchema, 'GET', '/account/tokens'),
  setProviderToken: (provider: Provider, token: string) => empty('PUT', `/account/tokens/${provider}`, { token }),
  removeProviderToken: (provider: Provider) => empty('DELETE', `/account/tokens/${provider}`),
  apiTokens: () => json(ApiTokensResponseSchema, 'GET', '/account/api-tokens'),
  createApiToken: (name: string) => json(CreateApiTokenResponseSchema, 'POST', '/account/api-tokens', { name }),
  revokeApiToken: (id: string) => empty('DELETE', `/account/api-tokens/${seg(id)}`),
  oauthRequest: (search: string) => json(AuthorizeInfoResponseSchema, 'GET', `/oauth/authorize${search}`),
  oauthDecide: (params: Record<string, string>, allow: boolean) => json(AuthorizeDecisionResponseSchema, 'POST', '/oauth/authorize', { ...params, allow }),
  listChats: () => json(ChatsResponseSchema, 'GET', '/chats'),
  createChat: (body: CreateChatRequest) => json(ChatResponseSchema, 'POST', '/chats', body),
  getChat: (id: string) => json(ChatDetailResponseSchema, 'GET', `/chats/${seg(id)}`),
  sendChatMessage: (id: string, body: SendChatMessageRequest) => json(ChatResponseSchema, 'POST', `/chats/${seg(id)}/messages`, body),
  stopChat: (id: string) => empty('POST', `/chats/${seg(id)}/stop`, {}),
  deleteChat: (id: string) => empty('DELETE', `/chats/${seg(id)}`),
  connectedApps: () => json(ConnectedAppsResponseSchema, 'GET', '/account/connected-apps'),
  disconnectApp: (id: string) => empty('DELETE', `/account/connected-apps/${seg(id)}`),
  machineStatus: (id: string) => json(MachineStatusResponseSchema, 'GET', `/machines/${seg(id)}/status`),
  machineLogs: (id: string, which: 'agent' | 'comfyui') => json(LogsResponseSchema, 'GET', `/machines/${seg(id)}/logs?which=${which}&lines=200`),
  updateAgent: (id: string) => json(UpdateAgentResponseSchema, 'POST', `/machines/${seg(id)}/update-agent`),
  updateComfyui: (id: string) => json(UpdateComfyResponseSchema, 'POST', `/machines/${seg(id)}/update-comfyui`),
  setIdleStop: (id: string, minutes: number) => json(IdleStopResponseSchema, 'PUT', `/machines/${seg(id)}/idle-stop`, { minutes }),
  machineModels: (id: string) => json(InstalledModelsSchema, 'GET', `/machines/${seg(id)}/models`),
  setModelFamily: (id: string, body: SetModelFamilyRequest) => empty('PUT', `/machines/${seg(id)}/models/family`, body),
  deleteModel: (id: string, body: DeleteModelRequest) => json(DeleteModelResponseSchema, 'POST', `/machines/${seg(id)}/models/delete`, body),
  machineFiles: (id: string, kind: MachineFileKind) => json(MachineFilesSchema, 'GET', `/machines/${seg(id)}/files?kind=${kind}`),
  deleteMachineFiles: (id: string, body: DeleteFilesRequest) => json(DeleteFilesResponseSchema, 'POST', `/machines/${seg(id)}/files/delete`, body),
  machineCatalog: (id: string) => json(CatalogResponseSchema, 'GET', `/machines/${seg(id)}/catalog`),
  installCatalogEntry: (id: string, entryId: string) => json(DownloadResponseSchema, 'POST', `/machines/${seg(id)}/catalog/${seg(entryId)}/install`),
  startDownload: (id: string, body: StartDownloadRequest) => json(DownloadResponseSchema, 'POST', `/machines/${seg(id)}/downloads`, body),
  listDownloads: (id: string) => json(DownloadsResponseSchema, 'GET', `/machines/${seg(id)}/downloads`),
  cancelDownload: (id: string, downloadId: string) => empty('DELETE', `/machines/${seg(id)}/downloads/${seg(downloadId)}`),
  pushKey: () => json(PushKeyResponseSchema, 'GET', '/push/key'),
  savePushSubscription: (subscription: PushSubscriptionJSON) => empty('POST', '/push/subscriptions', subscription),
  deletePushSubscription: (endpoint: string) => empty('DELETE', '/push/subscriptions', { endpoint }),
  testPush: (endpoint: string) => empty('POST', '/push/test', { endpoint }),
  listSessions: () => json(DeviceSessionsResponseSchema, 'GET', '/account/sessions'),
  signOutDevice: (id: string) => empty('DELETE', `/account/sessions/${seg(id)}`),
  signOutOtherDevices: () => empty('POST', '/account/sessions/sign-out-others'),
  listGallery: (filter: { recipeId?: string; machineId?: string; before?: string }) => {
    const query = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => Boolean(e[1])));
    return json(GalleryResponseSchema, 'GET', `/gallery${query.size ? `?${query.toString()}` : ''}`);
  },
  getGalleryImage: (id: string) => json(GalleryImageResponseSchema, 'GET', `/gallery/${seg(id)}`),
  deleteGalleryImage: (id: string, fromMachine: boolean) => empty('DELETE', `/gallery/${seg(id)}?fromMachine=${String(fromMachine)}`),
  machineStarters: (id: string) => json(StartersResponseSchema, 'GET', `/machines/${seg(id)}/starters`),
  workflowTemplates: (machineId: string) => json(WorkflowTemplatesResponseSchema, 'GET', `/machines/${seg(machineId)}/templates`),
  importWorkflow: (body: ImportWorkflowRequest) => json(ImportWorkflowResponseSchema, 'POST', '/recipes/import', body),
  recipeFromStarter: (body: FromStarterRequest) => json(RecipeResponseSchema, 'POST', '/recipes/from-starter', body),
  listShares: (id: string) => json(RecipeSharesResponseSchema, 'GET', `/recipes/${seg(id)}/shares`),
  shareRecipe: (id: string, username: string) => json(RecipeShareResponseSchema, 'POST', `/recipes/${seg(id)}/shares`, { username }),
  unshareRecipe: (id: string, username: string) => empty('DELETE', `/recipes/${seg(id)}/shares/${seg(username)}`),
  sharedRecipes: () => json(SharedRecipesResponseSchema, 'GET', '/shared-recipes'),
  getSharedRecipe: (id: string) => json(SharedRecipeDetailSchema, 'GET', `/shared-recipes/${seg(id)}`),
  copySharedRecipe: (id: string, body: CopySharedRequest) => json(RecipeResponseSchema, 'POST', `/shared-recipes/${seg(id)}/copy`, body),
  nodePacks: (machineId: string) => json(NodePacksResponseSchema, 'GET', `/machines/${seg(machineId)}/node-packs`),
  searchNodePacks: (machineId: string, q: string) => json(NodePackSearchResponseSchema, 'GET', `/machines/${seg(machineId)}/node-packs/search?q=${seg(q)}`),
  enableNodePacks: (machineId: string) => empty('POST', `/machines/${seg(machineId)}/node-packs/enable`, {}),
  nodeInstalls: (machineId: string) => json(NodeInstallsResponseSchema, 'GET', `/machines/${seg(machineId)}/node-installs`),
  requestNodeInstall: (machineId: string, packId: string) => json(NodeInstallResponseSchema, 'POST', `/machines/${seg(machineId)}/node-installs`, { packId }),
  decideNodeInstall: (machineId: string, installId: string, approve: boolean) =>
    json(NodeInstallResponseSchema, 'POST', `/machines/${seg(machineId)}/node-installs/${seg(installId)}/${approve ? 'approve' : 'decline'}`, {}),
  accountSettings: () => json(AccountSettingsResponseSchema, 'GET', '/account/settings'),
  saveAccountSettings: (settings: AccountSettings) => json(AccountSettingsResponseSchema, 'PATCH', '/account/settings', settings),
  listInputs: (machineId: string) => json(InputImagesResponseSchema, 'GET', `/machines/${seg(machineId)}/inputs`),
  uploadInput: (machineId: string, file: File) => upload(InputImageResponseSchema, `/machines/${seg(machineId)}/inputs?name=${seg(file.name)}`, file),
  listRecipes: () => json(RecipesResponseSchema, 'GET', '/recipes'),
  getRecipe: (id: string, version?: number) => json(RecipeDetailResponseSchema, 'GET', `/recipes/${seg(id)}${version ? `?version=${version}` : ''}`),
  deleteRecipe: (id: string) => empty('DELETE', `/recipes/${seg(id)}`),
  validateRecipe: (id: string, body: ValidateRecipeRequest) => json(ValidationResultSchema, 'POST', `/recipes/${seg(id)}/validate`, body),
  startRun: (id: string, body: StartRunRequest) => json(RunResponseSchema, 'POST', `/recipes/${seg(id)}/runs`, body),
  listRuns: (recipeId: string) => json(RunsResponseSchema, 'GET', `/runs?recipeId=${seg(recipeId)}`),
  getRun: (id: string) => json(RunResponseSchema, 'GET', `/runs/${seg(id)}`),
  startBatch: (recipeId: string, body: StartBatchRequest) => json(BatchResponseSchema, 'POST', `/recipes/${seg(recipeId)}/batches`, body),
  getBatch: (recipeId: string, batchId: string) => json(BatchResponseSchema, 'GET', `/recipes/${seg(recipeId)}/batches/${seg(batchId)}`),
  rerun: (id: string) => json(RunResponseSchema, 'POST', `/runs/${seg(id)}/rerun`, {}),
  cancelRun: (id: string) => json(RunResponseSchema, 'POST', `/runs/${seg(id)}/cancel`, {}),
};

/** A small WebP preview of an image in the machine's output or input folder (doc 47). */
export function machineFileViewUrl(id: string, kind: MachineFileKind, name: string): string {
  return `/api/v1/machines/${seg(id)}/files/view?kind=${kind}&name=${seg(name)}`;
}

/** A readable message for any thrown value. */
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}
